import { __rest } from 'tslib';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { forwardRef, useRef, useState, useEffect, useImperativeHandle, useMemo } from 'react';
import { useFloating, offset, flip, shift, limitShift, autoUpdate, useTransitionStyles, useDismiss, useClick, useInteractions } from '@floating-ui/react';
import { hide } from '@floating-ui/react-dom';
import { SfCalendarIcon, CalendarIcon } from '@ninja/icons/web';
import { getDateUtilsConfig, useMask, useReactFormController, parseIsoDateTime, calculateAge, getIsoDateTime } from '@ninja/utilities';
import { useResponsiveView } from './useResponsiveView.js';
import { useWebUIConfigOptions } from './ThemeProvider2.js';
import { FormInputGroup } from './FormInputGroup2.js';
import { InternalTextField } from './TextField2.js';
import ResponsiveRenderViewContainer from './DatePicker3.js';
import { CalendarView } from './components10.js';
import styles from './DatePicker4.js';
import './utils2.js';
import { getMask } from './utils3.js';
import { getDateFormatter } from './utils4.js';
import { parse } from './utils5.js';

const DatePickerComponent = (props, ref) => {
    var _a;
    const inputRef = useRef(null);
    const { dateFormat, dateTimeFormat } = getDateUtilsConfig();
    const { label, isRequired, reserveLabelSpacing, popover, popoverVariant, layoutClassName, defaultMonth, valueType, size, format, disabled, placeholder, fromDate, toDate, variant = 'default', isClearable = true, isDateOfBirth, highlighted, readonly, viewMode: pickerViewMode = 'days', autoSelectTime = false, calendars = ['gregorian'], selectedCalendar, onCalendarChange, mode = 'single', maxSelectionRangeDays, testId, } = props;
    const enableTimeSelection = pickerViewMode === 'days' && ((_a = props.enableTimeSelection) !== null && _a !== void 0 ? _a : false);
    const calenderViewRef = useRef(null);
    const mask = getMask(pickerViewMode, enableTimeSelection);
    const placeHolder = placeholder !== null && placeholder !== void 0 ? placeholder : mask.placeholder;
    const { applyMask, extractValue } = useMask({ mask: mask.value });
    const isDateOnly = format === 'date-only';
    const dateFormatter = getDateFormatter(pickerViewMode, enableTimeSelection);
    const isIntervalPicker = mode === 'interval';
    const { isRtlLocale } = useWebUIConfigOptions();
    const _b = useReactFormController(props), { value: tempOriginalValue, onChange, onBlur: onInputBlur, hasErrors, formId } = _b, fieldProps = __rest(_b, ["value", "onChange", "onBlur", "hasErrors", "formId"]);
    // Single Picker or Interval Picker Start Date
    const originalValue = isIntervalPicker
        ? tempOriginalValue === null || tempOriginalValue === void 0 ? void 0 : tempOriginalValue.startDate
        : tempOriginalValue;
    const value = !originalValue
        ? undefined
        : typeof originalValue === 'string'
            ? parseIsoDateTime(originalValue)
            : originalValue;
    const formattedValue = value ? dateFormatter(value, { calendar: selectedCalendar }) : undefined;
    // Interval Picker End Date
    const orignalSecondaryValue = isIntervalPicker
        ? tempOriginalValue === null || tempOriginalValue === void 0 ? void 0 : tempOriginalValue.endDate
        : undefined;
    const secondaryValue = !orignalSecondaryValue
        ? undefined
        : typeof orignalSecondaryValue === 'string'
            ? parseIsoDateTime(orignalSecondaryValue)
            : orignalSecondaryValue;
    const formattedSecondaryValue = isIntervalPicker && secondaryValue ? dateFormatter(secondaryValue, { calendar: selectedCalendar }) : undefined;
    const parseValue = parse(pickerViewMode, enableTimeSelection);
    const [internalValue, setInternalValue] = useState(formattedValue);
    const [internalSecondaryValue, setInternalSecondaryValue] = useState();
    const [internalSelectedCalendar, setInternalSelectedCalendar] = useState(selectedCalendar);
    useEffect(() => {
        setInternalValue(formattedValue);
    }, [formattedValue]);
    useEffect(() => {
        setInternalSecondaryValue(formattedSecondaryValue);
    }, [formattedSecondaryValue]);
    const [isFocused, setIsFocused] = useState(false);
    const ignoreResetValue = useRef(false);
    const { sm } = useResponsiveView();
    useEffect(() => {
        const onFocus = () => {
            var _a;
            ignoreResetValue.current = false;
            setInternalValue(formattedValue);
            setInternalSecondaryValue(formattedSecondaryValue);
            setIsFocused(true);
            (_a = props.onFocus) === null || _a === void 0 ? void 0 : _a.call(props);
            setInternalSelectedCalendar(selectedCalendar);
        };
        const onBlur = () => {
            var _a;
            setIsFocused(false);
            (_a = props.onBlur) === null || _a === void 0 ? void 0 : _a.call(props);
        };
        if (inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) {
            inputRef.current.addEventListener('focus', onFocus);
            inputRef.current.addEventListener('blur', onBlur);
        }
        return () => {
            if (inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) {
                inputRef.current.removeEventListener('focus', onFocus);
                inputRef.current.removeEventListener('blur', onBlur);
            }
        };
    }, [inputRef === null || inputRef === void 0 ? void 0 : inputRef.current, formattedValue, formattedSecondaryValue]);
    useImperativeHandle(ref, () => ({
        focus: () => { var _a; return (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus(); },
    }));
    const { x, y, refs, strategy, context, middlewareData } = useFloating({
        open: isFocused,
        onOpenChange: (isOpen) => {
            if (variant === 'iconOnly' && !isOpen) {
                setIsFocused(isOpen);
            }
        },
        placement: 'bottom-start',
        middleware: [
            offset({
                mainAxis: 4,
            }),
            flip(),
            shift({
                limiter: limitShift(),
            }),
            hide(),
        ],
        whileElementsMounted: autoUpdate,
    });
    const { isMounted, styles: floatingStyles } = useTransitionStyles(context);
    const dismiss = useDismiss(context);
    const click = useClick(context, {
        event: 'mousedown',
        toggle: false,
    });
    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);
    const isElementOutOfScope = useMemo(() => { var _a; return (_a = middlewareData.hide) === null || _a === void 0 ? void 0 : _a.referenceHidden; }, [middlewareData]);
    const calendarViewStyles = useMemo(() => {
        return Object.assign({ position: strategy, top: y !== null && y !== void 0 ? y : 0, left: x !== null && x !== void 0 ? x : 0, width: 'max-content' }, floatingStyles);
    }, [strategy, floatingStyles, x, y]);
    const ageLabel = value && internalValue && isDateOfBirth ? `(${calculateAge(value)})` : undefined;
    const onCalendarClose = () => {
        var _a;
        setIsFocused(false);
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.blur();
    };
    const calendarViewComponent = (jsx("div", { onMouseDown: (e) => e.preventDefault(), children: jsx(ResponsiveRenderViewContainer, { pickerViewMode: pickerViewMode, enableTimeSelection: enableTimeSelection, calendarViewStyles: calendarViewStyles, calenderViewRef: calenderViewRef, getFloatingProps: getFloatingProps, isElementOutOfScope: isElementOutOfScope, isFocused: isFocused, isMounted: isMounted, onCalendarClose: onCalendarClose, setFloating: refs.setFloating, isMobile: sm, multiCalendar: calendars && calendars.length > 1, isIntervalPicker: isIntervalPicker, children: jsx(CalendarView, { pickerViewMode: pickerViewMode, defaultMonth: value !== null && value !== void 0 ? value : defaultMonth, enableTimeSelection: enableTimeSelection, currentDate: value, currentEndDate: secondaryValue, onChange: (newDateValue, calendar, newSecondaryDateValue) => {
                    onCalendarChange && calendar && onCalendarChange(calendar);
                    setInternalValue(extractValue(dateFormatter(newDateValue, { calendar: calendar })));
                    if (isIntervalPicker) {
                        newSecondaryDateValue &&
                            setInternalSecondaryValue(extractValue(dateFormatter(newSecondaryDateValue, { calendar: calendar })));
                        const startDate = valueType === 'date' ? newDateValue : getIsoDateTime(newDateValue, isDateOnly);
                        const endDate = valueType === 'date' ? newSecondaryDateValue : getIsoDateTime(newSecondaryDateValue, isDateOnly);
                        onChange({ startDate, endDate });
                    }
                    else {
                        if (valueType === 'date')
                            onChange(newDateValue);
                        else
                            onChange(getIsoDateTime(newDateValue, isDateOnly));
                    }
                    if (variant === 'iconOnly') {
                        setIsFocused(false);
                    }
                    else {
                        setTimeout(() => {
                            var _a;
                            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.blur();
                        });
                    }
                }, fromDate: fromDate, toDate: toDate, onCancelSelection: () => {
                    var _a;
                    if (variant === 'iconOnly')
                        setIsFocused(false);
                    else
                        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.blur();
                }, autoSelectTime: autoSelectTime, calendars: calendars, selectedCalendar: internalSelectedCalendar, onCalendarChange: setInternalSelectedCalendar, isIntervalPicker: isIntervalPicker, maxSelectionRangeDays: maxSelectionRangeDays }) }) }));
    return (jsxs(Fragment, { children: [variant === 'default' && (jsx(FormInputGroup, { label: label, isRequired: isRequired, popover: popover, popoverVariant: popoverVariant, layoutClassName: layoutClassName, reserveLabelSpacing: reserveLabelSpacing, formId: formId, hasErrors: hasErrors, highlighted: highlighted, children: jsxs(InternalTextField, Object.assign({ testId: testId, ref: inputRef, value: isIntervalPicker ? `${internalValue !== null && internalValue !== void 0 ? internalValue : ''} - ${internalSecondaryValue !== null && internalSecondaryValue !== void 0 ? internalSecondaryValue : ''}` : internalValue, mask: isIntervalPicker ? `${mask.value} - ${mask.value}` : mask.value, placeholder: isIntervalPicker
                        ? `${isRtlLocale ? 'من' : 'From'} DD/MM/YYYY - ${isRtlLocale ? 'إلى' : 'To'} DD/MM/YYYY`
                        : placeHolder, 
                    // for interval picker currently disabled input entry
                    readOnly: readonly || sm || isIntervalPicker, size: size, isClearable: isClearable, hasErrors: hasErrors, onClearValue: () => {
                        onChange(undefined);
                        if (!isFocused) {
                            ignoreResetValue.current = true;
                        }
                    }, disabled: disabled, endIcon: SfCalendarIcon, onChange: (val) => {
                        setInternalValue(val);
                    }, onBlur: () => {
                        // for interval picker currently input is readOnly
                        if (!ignoreResetValue.current && !isIntervalPicker) {
                            if (!internalValue && value) {
                                onChange(undefined);
                            }
                            const newFormattedValue = applyMask(internalValue !== null && internalValue !== void 0 ? internalValue : '').result;
                            const { isValid, date: validDate } = parseValue(newFormattedValue, undefined, selectedCalendar, internalValue);
                            if (isValid) {
                                if (valueType === 'date')
                                    onChange(validDate);
                                else
                                    onChange(getIsoDateTime(validDate, isDateOnly));
                            }
                            else {
                                setInternalValue(formattedValue);
                            }
                        }
                        onInputBlur();
                    }, floatingDivRef: refs.setReference, getFloatingReferenceProps: getReferenceProps }, fieldProps, { children: [calendarViewComponent, ageLabel && (jsx("div", { className: styles.ageLabel(hasErrors), style: {
                                marginInlineStart: `${(enableTimeSelection ? dateTimeFormat.length : dateFormat.length) * 11}px`,
                            }, children: ageLabel }))] })) })), variant === 'iconOnly' && (jsxs(Fragment, { children: [jsx(CalendarIcon, { className: "w-6 h-6 cursor-pointer nj-text-brand", onClick: () => {
                            setIsFocused(true);
                        } }), calendarViewComponent] }))] }));
};
const DatePicker = forwardRef(DatePickerComponent);

export { DatePicker };
